import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Descriptions, Divider, Input, message, Modal, Pagination, Select, Tag } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Check,
  ExternalLink,
  History,
  Search,
  Settings
} from 'lucide-react';
import ZapierAppDirectory from './ZapierAppDirectory';
import { IntegrationIcon } from '../../../integration_settings/integrationIcon';
import {
  integrationCatalogKey,
  integrationInfoForCode
} from '../../../integration_settings/integrationsInfo';
import { onArticleClick } from '../../../../utils/HelpArticleButton';

import styles from './index.module.css';
import { connectJiraProject, reauthoriseJira } from '../../../integration_settings/reauthoriseJira';

const PROJECTS_PER_PAGE = 50;

const OAUTH_CONNECTORS = {
  asana: { provider: 'asana', type: 'Asana' },
  clickup: { provider: 'clickup', type: 'Clickup' },
  github: { provider: 'github', type: 'Github' },
  jira: { provider: 'atlassian_oauth2', type: 'Jira' },
  linear: { provider: 'linear', type: 'Linear' },
  slack: { provider: 'slack', type: 'Slack' },
  slack_legacy: { provider: 'slack', type: 'Slack' },
  team: { provider: 'microsoft_graph', type: 'Team' },
  teamwork: { provider: 'teamwork', type: 'Teamwork' },
  trello: { provider: 'trello', type: 'Trello' }
};

const MANUAL_AUTH_CONNECTORS = {
  monday: {
    providerName: 'monday',
    path: 'monday_auth',
    title: 'Authenticate Monday',
    tokenLabel: 'API V2 Token',
    tokenPlaceholder: 'Enter your Monday API token'
  },
  mondaynative: {
    providerName: 'monday',
    path: 'monday_auth',
    title: 'Authenticate Monday',
    tokenLabel: 'API V2 Token',
    tokenPlaceholder: 'Enter your Monday API token'
  },
  mondaycom: {
    providerName: 'monday',
    path: 'monday_auth',
    title: 'Authenticate Monday',
    tokenLabel: 'API V2 Token',
    tokenPlaceholder: 'Enter your Monday API token'
  },
  productive: {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  },
  productivenative: {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  },
  customproductive: {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  },
  customproductiveio: {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  },
  'custom.productive': {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  },
  'custom.productiveio': {
    providerName: 'productive',
    path: 'productive_auth',
    title: 'Authenticate Productive',
    tokenLabel: 'API token',
    tokenPlaceholder: 'Enter your Productive API token',
    requiresOrganizationId: true
  }
};

export default class Manage extends React.Component {
  static propTypes = {
    apiKey: PropTypes.string,
    connected: PropTypes.bool,
    orgName: PropTypes.string,
    orgId: PropTypes.number,
    integrationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    title: PropTypes.string,
    about: PropTypes.string,
    articleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    projectConnections: PropTypes.array,
    organizationProjects: PropTypes.array,
    authProviders: PropTypes.array,
    jiraAuthentication: PropTypes.object,
  };

  state = {
    projectConnections: this.props.projectConnections || [],
    authenticatedProviderNames: [],
    manualAuthModalVisible: false,
    manualAuthToken: '',
    productiveOrganizationId: '',
    isAuthenticating: false,
    projectPage: 1,
    projectSearch: '',
    projectSort: 'updated'
  };

  componentDidUpdate(prevProps) {
    if (prevProps.integrationId !== this.props.integrationId) {
      this.setState({
        projectConnections: this.props.projectConnections || [],
        authenticatedProviderNames: [],
        manualAuthModalVisible: false,
        manualAuthToken: '',
        productiveOrganizationId: '',
        isAuthenticating: false,
        projectPage: 1,
        projectSearch: '',
        projectSort: 'updated'
      });
    }
  }

  removeIntegration = () => {
    const { orgId, integrationId } = this.props;

    fetch(`/organizations/${orgId}/integrations/${integrationId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': (
          document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
        )?.content
      }
    })
      .then(response => {
        if (!response.ok && !response.redirected) {
          throw Error(response.statusText);
        }

        window.location.href = `/settings/organizations/${orgId}/integrations`;
      })
      .catch(() => {
        message.error('Something went wrong, please try again.', 2.5);
      });
  };

  confirmRemoveIntegration = () => {
    Modal.confirm({
      title: this.props.code === 'jira' ? 'Remove Jira integration?' : 'Disconnect this integration?',
      content: this.props.code === 'jira'
        ? `This removes settings for ${this.state.projectConnections.length} projects and ${this.props.jiraAuthentication?.task_links_count || 0} task links. Jira issues will remain in Jira.`
        : 'This will disconnect the integration and all project connections from the organization.',
      okText: this.props.code === 'jira' ? 'Remove Jira integration' : 'Disconnect integration',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: this.removeIntegration
    });
  };

  projectConnectionProjectId = projectConnection =>
    projectConnection?.project_id ||
    projectConnection?.projectId ||
    projectConnection?.['project_id'] ||
    projectConnection?.['projectId'];

  projectId = project => project?.id || project?.['id'];

  projectName = project => project?.name || project?.['name'] || '';

  projectDate = (project, key) => project?.[key] || project?.[key.replace('_', '')];

  projectIntegrationSettingsKey = () => {
    switch (this.props.code) {
      case 'slack_legacy':
        return 'slack';
      case 'team':
        return 'teams';
      case 'trello':
        return 'trellonative';
      default:
        return this.props.code;
    }
  };

  projectIntegrationSettingsPath = project =>
    `/projects/${this.projectId(project)}/integrations?open_integration=${encodeURIComponent(
      this.projectIntegrationSettingsKey()
    )}`;

  projectConnectPath = project => {
    const oauthConnector = OAUTH_CONNECTORS[this.props.code];
    if (!oauthConnector) return this.projectIntegrationSettingsPath(project);

    const params = new URLSearchParams({
      project: String(this.projectId(project)),
      organization: String(this.props.orgId),
      type: oauthConnector.type
    });
    return `/auth/${oauthConnector.provider}?${params.toString()}`;
  };

  integrationAuthPath = () => {
    const oauthConnector = OAUTH_CONNECTORS[this.props.code];
    if (!oauthConnector) return null;

    const params = new URLSearchParams({
      organization: String(this.props.orgId),
      type: oauthConnector.type,
      origin: window.location.pathname + window.location.search
    });
    return `/auth/${oauthConnector.provider}?${params.toString()}`;
  };

  integrationAuthMethod = () =>
    OAUTH_CONNECTORS[this.props.code] ? 'post' : undefined;

  manualAuthConfig = () => MANUAL_AUTH_CONNECTORS[this.props.code];

  integrationAuthProviderName = () => {
    const manualAuthConfig = this.manualAuthConfig();
    if (manualAuthConfig) return manualAuthConfig.providerName;

    return OAUTH_CONNECTORS[this.props.code]?.provider;
  };

  integrationAuthenticated = () => {
    const providerName = this.integrationAuthProviderName();
    if (!providerName) return true;

    return [
      ...(this.props.authProviders || []),
      ...this.state.authenticatedProviderNames
    ].includes(providerName);
  };

  projectConnectionForProject = project =>
    this.state.projectConnections.find(
      projectConnection =>
        String(this.projectConnectionProjectId(projectConnection)) ===
        String(this.projectId(project))
    );

  paginatedProjects = () => {
    const projects = this.filteredProjects();
    const start = (this.state.projectPage - 1) * PROJECTS_PER_PAGE;
    return projects.slice(
      start,
      start + PROJECTS_PER_PAGE
    );
  };

  connectionStatusRank = project => {
    if (this.projectConnectionForProject(project)) return 1;
    return 2;
  };

  filteredProjects = () => {
    const { projectSearch, projectSort } = this.state;
    const query = projectSearch.trim().toLowerCase();

    return [...(this.props.organizationProjects || [])]
      .filter(project => this.projectName(project).toLowerCase().includes(query))
      .sort((a, b) => {
        if (projectSort === 'connection') {
          const rankDiff =
            this.connectionStatusRank(a) - this.connectionStatusRank(b);
          if (rankDiff !== 0) return rankDiff;

          return this.projectName(a).localeCompare(this.projectName(b));
        }

        if (projectSort === 'name') {
          return this.projectName(a).localeCompare(this.projectName(b));
        }

        return (
          new Date(this.projectDate(b, 'updated_at') || 0).getTime() -
          new Date(this.projectDate(a, 'updated_at') || 0).getTime()
        );
      });
  };

  csrfToken = () =>
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
      ?.content;

  openManualAuthModal = () => {
    this.setState({
      manualAuthModalVisible: true,
      manualAuthToken: '',
      productiveOrganizationId: ''
    });
  };

  closeManualAuthModal = () => {
    if (this.state.isAuthenticating) return;

    this.setState({ manualAuthModalVisible: false });
  };

  manualAuthSubmitDisabled = () => {
    const config = this.manualAuthConfig();
    if (!config) return true;
    if (!this.state.manualAuthToken.trim()) return true;

    return (
      config.requiresOrganizationId &&
      !this.state.productiveOrganizationId.trim()
    );
  };

  submitManualAuth = () => {
    const config = this.manualAuthConfig();
    if (!config || this.manualAuthSubmitDisabled()) return;

    const payload: {
      auth_token: string;
      productive_organization_id?: string;
    } = {
      auth_token: this.state.manualAuthToken.trim()
    };
    if (config.requiresOrganizationId) {
      payload.productive_organization_id = this.state.productiveOrganizationId.trim();
    }

    this.setState({ isAuthenticating: true });

    fetch(`/organizations/${this.props.orgId}/integrations/${config.path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw Error(data.error || response.statusText);
          });
        }

        return response.json();
      })
      .then(() => {
        this.setState(prevState => ({
          authenticatedProviderNames: [
            ...prevState.authenticatedProviderNames.filter(
              providerName => providerName !== config.providerName
            ),
            config.providerName
          ],
          manualAuthModalVisible: false,
          manualAuthToken: '',
          productiveOrganizationId: '',
          isAuthenticating: false
        }));
        message.success(`${this.props.title} authenticated successfully.`, 2.5);
      })
      .catch(error => {
        this.setState({ isAuthenticating: false });
        message.error(
          error.message || `Could not authenticate ${this.props.title}.`,
          2.5
        );
      });
  };

  relativeDate = value => {
    if (!value) return null;

    const diffDays = Math.max(
      0,
      Math.floor((Date.now() - new Date(value).getTime()) / 86400000)
    );
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    return `${diffDays} days ago`;
  };

  projectMeta = project => {
    const updated = this.relativeDate(this.projectDate(project, 'updated_at'));
    const created = this.relativeDate(this.projectDate(project, 'created_at'));
    return [updated && `Updated ${updated}`, created && `Created ${created}`]
      .filter(Boolean)
      .join(' · ');
  };

  disconnectProjectConnection = projectConnection => {
    const { orgId, integrationId } = this.props;

    const action = this.props.code === 'jira' && projectConnection.settings?.sync_paused ? 'resume' : 'disconnect';
    fetch(
      `/organizations/${orgId}/integrations/${integrationId}/project_integrations/${action}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (
            document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
          )?.content
        },
        body: JSON.stringify({ id: projectConnection.id })
      }
    )
      .then(response => {
        if (!response.ok) throw Error(response.statusText);

        if (this.props.code === 'jira') {
          this.setState(prevState => ({
            projectConnections: prevState.projectConnections.map(connection => connection.id === projectConnection.id
              ? { ...connection, settings: { ...connection.settings, sync_paused: action === 'disconnect' } }
              : connection)
          }));
          message.success(action === 'resume' ? 'Jira syncing resumed.' : 'Project disconnected. Existing task links have been preserved.', 2.5);
          return;
        }
        if (this.state.projectConnections.length <= 1) {
          window.location.href = `/settings/organizations/${orgId}/integrations`;
          return;
        }

        this.setState(prevState => ({
          projectConnections: prevState.projectConnections.filter(
            connection => connection.id !== projectConnection.id
          )
        }));
        message.success('Project disconnected successfully.', 2.5);
      })
      .catch(() => {
        message.error('Something went wrong, please try again.', 2.5);
      });
  };

  getZapierSettings = () => {
    const { apiKey } = this.props;
    const colorTheme = localStorage.getItem('theme');
    const colorSchemeZapier = colorTheme === 'dark-theme' ? 'dark' : 'light';

    return (
      <>
        <div className={styles.moreInfoBox}>
          <p className={styles.about}>
            Connecting Zapier to your BugHerd account couldn't be easier. Choose
            from one of the popular zaps below and follow the instructions to
            set up your account.
          </p>
          <span className={styles.need}>What you'll need...</span>
          <ul className={styles.needList}>
            <li>
              - A Zapier account (
              <a
                href="https://zapier.com/sign-up/"
                target="_blank"
                rel="noreferrer"
                className={styles.zapierLink}
              >
                sign up free here
              </a>
              )
            </li>
            <li>
              - Your BugHerd API Key:{' '}
              <span className={styles.apiKey}>{apiKey}</span>
            </li>
          </ul>
        </div>
        <div className={styles.embedContainer}>
          <ZapierAppDirectory theme={colorSchemeZapier} />
        </div>
      </>
    );
  };

  getConnectedElements = () => (
    <div className={styles.connectedStatus}>
      <CheckCircleFilled className={styles.connectedIcon} />
      <span className={styles.connectedLabel}>Connected</span>
    </div>
  );

  getManualAuthModal = () => {
    const config = this.manualAuthConfig();
    if (!config) return null;

    return (
      <Modal
        visible={this.state.manualAuthModalVisible}
        title={config.title}
        okText="Authenticate"
        cancelText="Cancel"
        confirmLoading={this.state.isAuthenticating}
        okButtonProps={{ disabled: this.manualAuthSubmitDisabled() }}
        onOk={this.submitManualAuth}
        onCancel={this.closeManualAuthModal}
      >
        {config.requiresOrganizationId && (
          <div className={styles.manualAuthField}>
            <label htmlFor="productive-organization-id">Organization ID</label>
            <Input
              id="productive-organization-id"
              value={this.state.productiveOrganizationId}
              onChange={event =>
                this.setState({ productiveOrganizationId: event.target.value })
              }
              autoComplete="off"
              disabled={this.state.isAuthenticating}
            />
          </div>
        )}
        <div className={styles.manualAuthField}>
          <label htmlFor="manual-auth-token">{config.tokenLabel}</label>
          <Input.Password
            id="manual-auth-token"
            value={this.state.manualAuthToken}
            onChange={event =>
              this.setState({ manualAuthToken: event.target.value })
            }
            placeholder={config.tokenPlaceholder}
            autoComplete="off"
            disabled={this.state.isAuthenticating}
            onPressEnter={this.submitManualAuth}
          />
        </div>
      </Modal>
    );
  };

  getOrganizationOptions = () => {
    const {
      projectPage,
      projectSearch,
      projectSort
    } = this.state;
    const totalProjects = this.filteredProjects().length;

    return (
      <div className={styles.optionsBox}>
        <div className={styles.projectsBox}>
          <div className={styles.projectsHeader}>
            <h3 className={styles.optionsHeader}>Projects</h3>
          </div>
          <div className={styles.projectToolbar}>
            <Input
              className={styles.projectSearch}
              prefix={<Search className={styles.searchIcon} />}
              value={projectSearch}
              onChange={event =>
                this.setState({ projectSearch: event.target.value, projectPage: 1 })
              }
              placeholder="Search projects"
            />
            <Select
              className={styles.projectSort}
              value={projectSort}
              onChange={value =>
                this.setState({ projectSort: value, projectPage: 1 })
              }
              options={[
                { value: 'updated', label: 'Sort: Last updated' },
                { value: 'name', label: 'Sort: Name' },
                { value: 'connection', label: 'Sort: Connection status' }
              ]}
            />
          </div>
          <div className={styles.projectList}>
            {this.paginatedProjects().map(project => {
              const projectConnection = this.projectConnectionForProject(project);
              return (
                <div
                  key={this.projectId(project)}
                  className={styles.projectRow}
                >
                  <div className={styles.projectDetails}>
                    <div className={styles.projectTitleLine}>
                      <span>{this.projectName(project)}</span>
                      {projectConnection ? (
                        <span className={styles.connectedPill}>
                          <Check className={styles.pillIcon} />
                          {this.props.code === 'jira' && projectConnection.settings?.sync_paused ? 'Syncing paused' : 'Connected'}
                        </span>
                      ) : null}
                    </div>
                    {this.projectMeta(project) && (
                      <p className={styles.projectMeta}>{this.projectMeta(project)}</p>
                    )}
                  </div>
                  {projectConnection ? (
                    <div className={styles.rowActions}>
                      <a
                        className={styles.projectSettingsLink}
                        href={this.projectIntegrationSettingsPath(project)}
                      >
                        <Button className={styles.manageProjectButton}>
                          <Settings className={styles.actionIcon} />
                          Manage
                        </Button>
                      </a>
                      <Button
                        danger
                        className={styles.disconnectProjectButton}
                        onClick={() => this.disconnectProjectConnection(projectConnection)}
                      >
                        {this.props.code === 'jira' && projectConnection.settings?.sync_paused ? 'Resume syncing' : 'Disconnect'}
                      </Button>
                    </div>
                  ) : this.props.code === 'jira' ? (
                    <Button
                      type="primary"
                      className={styles.connectProjectButton}
                      onClick={() =>
                        connectJiraProject(this.props.orgId, this.projectId(project)).catch(error =>
                          message.error(error.message || 'Could not connect Jira.')
                        )
                      }
                    >
                      Connect
                    </Button>
                  ) : (
                    <a
                      className={styles.projectSettingsLink}
                      href={this.projectConnectPath(project)}
                      rel="nofollow"
                      data-method={
                        OAUTH_CONNECTORS[this.props.code] ? 'post' : undefined
                      }
                    >
                      <Button type="primary" className={styles.connectProjectButton}>
                        Connect
                      </Button>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          {totalProjects > PROJECTS_PER_PAGE && (
            <Pagination
              className={styles.pagination}
              current={projectPage}
              pageSize={PROJECTS_PER_PAGE}
              total={totalProjects}
              showSizeChanger={false}
              onChange={page => this.setState({ projectPage: page })}
            />
          )}
        </div>
      </div>
    );
  };

  render() {
    const { connected, orgId, code, title, about, icon: iconProp, articleId } = this.props;
    const catalogKey = integrationCatalogKey(code);
    const icon = iconProp || integrationInfoForCode(code).icon;
    const isZapier = code === 'zapier';
    const integrationAuthPath = this.integrationAuthPath();
    const manualAuthConfig = this.manualAuthConfig();
    const showDisconnectAction = connected && (code === 'jira' || this.integrationAuthenticated());
    const showOAuthAuthenticationAction =
      (code !== 'jira' || !this.props.integrationId) && integrationAuthPath && !this.integrationAuthenticated();
    const showManualAuthenticationAction = !!manualAuthConfig;

    return (
      <div className={styles.manageContainer}>
        <div className={styles.manageIntegration}>
          <Link to={`/organizations/${orgId}/integrations`} replace>
            <Button type="link" className={styles.navigateBack}>
              {'< Back to Integrations'}
            </Button>
          </Link>
          <div className={styles.manageHeader}>
            <div className={styles.manageBox}>
              <div className={styles.logoBox}>
                <IntegrationIcon
                  name={catalogKey}
                  icon={icon}
                  integrationName={catalogKey}
                />
              </div>
              <div className={styles.moreInfo}>
                <h2 className={styles.moreInfoHeader}>{title}</h2>
                {about && <p className={styles.about}>{about}</p>}
                {connected && this.getConnectedElements()}
              </div>
            </div>
            <div className={styles.headerActions}>
              {connected && this.props.integrationId && (
                <Link
                  to={`/organizations/${orgId}/integrations/${this.props.integrationId}/activity`}
                >
                  <Button
                    type="link"
                    className={styles.activityLogButton}
                    icon={<History className={styles.activityLogIcon} />}
                  >
                    Activity log
                  </Button>
                </Link>
              )}
              {showDisconnectAction && (
                <Button
                  danger
                  shape="round"
                  onClick={this.confirmRemoveIntegration}
                >
                  {code === 'jira' ? 'Remove Jira integration' : 'Disconnect'}
                </Button>
              )}
              {code === 'jira' && this.props.integrationId && (
                <Button shape="round" type="primary"
                  onClick={() => reauthoriseJira(orgId, this.props.integrationId).catch(error => message.error(error.message || error.error || 'Could not re-authorise Jira.'))}>
                  Re-authorise Jira
                </Button>
              )}
              {showOAuthAuthenticationAction && (
                <a
                  className={styles.headerActionLink}
                  href={integrationAuthPath}
                  rel="nofollow"
                  data-method={this.integrationAuthMethod()}
                >
                  <Button type="primary" shape="round">
                    Authenticate
                  </Button>
                </a>
              )}
              {showManualAuthenticationAction && (
                <Button
                  type="primary"
                  shape="round"
                  onClick={this.openManualAuthModal}
                >
                  {this.integrationAuthenticated() ? 'Re-authenticate' : 'Authenticate'}
                </Button>
              )}
              {articleId && (
                <Button
                  type="link"
                  className={styles.helpGuideButton}
                  icon={<ExternalLink className={styles.helpGuideIcon} />}
                  onClick={() => onArticleClick({ articleId })}
                >
                  Help guide
                </Button>
              )}
            </div>
          </div>
          {code === 'jira' && this.props.integrationId && (
            <Card size="small" title="Jira connection" className={styles.jiraConnection}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Jira account">
                  {this.props.jiraAuthentication?.account || 'Unavailable'}
                </Descriptions.Item>
                <Descriptions.Item label="Connected by">
                  {this.props.jiraAuthentication?.owner || 'Unavailable'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={this.props.jiraAuthentication?.requires_reauth ? 'warning' : 'success'}>
                    {this.props.jiraAuthentication?.requires_reauth ? 'Re-authorisation required' : 'Connected'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Jira sites">
                  <div className={styles.jiraSites}>
                    {(this.props.jiraAuthentication?.sites || []).length
                      ? this.props.jiraAuthentication.sites.map(site => <Tag key={site}>{site}</Tag>)
                      : 'Unavailable'}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
          <Divider className={styles.headerDivider} />
          {isZapier ? this.getZapierSettings() : this.getOrganizationOptions()}
        </div>
        {this.getManualAuthModal()}
      </div>
    );
  }
}
